/**
 * SuperAdmin only: every email domain claimed on this orchestrator, in every
 * tenant (doc 28d §10.4). A domain has one owner across the orchestrator, and a
 * second claim answers 409 DOMAIN_CLAIMED without naming the holder; this list
 * is how an operator resolves such a dispute. Removing another tenant's claim
 * needs a reason (recorded in that tenant's activity log) and shows the same
 * preview of who changes sign-in method in that tenant.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Globe, RefreshCw, Search, Trash2 } from 'lucide-react';
import { useToast } from '../common/ToastContainer';
import RoutingPreviewDialog from './RoutingPreviewDialog';
import { DomainStatusBadge } from './ProviderRoutingCard';
import accessService, { confirmRequiredPreview, type ClaimedDomain, type RoutingPreview } from '../../services/access.service';
import { useTenantStore } from '../../stores/tenantStore';
import { apiErrorMessage } from '../../utils/apiError';
import { formatDate } from '../../utils/formatDate';
import { useSettingsStore } from '../../stores/settingsStore';
import { domainLabel } from '../../utils/signInRouting';

const ClaimedDomainsCard = ({ refreshKey, onChanged }: { refreshKey: number; onChanged: () => Promise<void> | void }) => {
  const toast = useToast();
  const tenants = useTenantStore((s) => s.tenants);
  const { getDateFormatSettings } = useSettingsStore();
  const dateSettings = getDateFormatSettings();
  const [rows, setRows] = useState<ClaimedDomain[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  const [target, setTarget] = useState<ClaimedDomain | null>(null);
  const [preview, setPreview] = useState<RoutingPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const tenantName = useCallback((id: string) => tenants.find((t) => t.id === id)?.name ?? id, [tenants]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setRows(await accessService.listClaimedDomains());
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load the claimed domains'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.domain, r.domainUnicode ?? '', r.tenantId, tenantName(r.tenantId)].some((v) => v.toLowerCase().includes(q)),
    );
  }, [rows, filter, tenantName]);

  const openRemove = async (r: ClaimedDomain) => {
    setTarget(r);
    setReason('');
    setPreview(null);
    setPreviewError(null);
    setPreviewLoading(true);
    try {
      setPreview(await accessService.getRoutingPreview({ providerId: r.providerId, removeDomain: r.domain }, r.tenantId));
    } catch (err) {
      setPreviewError(apiErrorMessage(err, 'preview unavailable'));
    } finally {
      setPreviewLoading(false);
    }
  };

  const remove = async () => {
    if (!target) return;
    try {
      setSaving(true);
      await accessService.removeIdentityProviderDomain(target.providerId, target.id, {
        tenantId: target.tenantId,
        reason: reason.trim(),
        ...(preview?.changed ? { confirm: true } : {}),
      });
      toast.success('Claim removed', `${domainLabel(target)} (${tenantName(target.tenantId)})`);
      setTarget(null);
      await load();
      await onChanged();
    } catch (err) {
      const fresh = confirmRequiredPreview(err);
      if (fresh) {
        setPreview(fresh);
        toast.error('Confirm again', 'The number of users affected changed; review the preview.');
      } else {
        toast.error('Remove failed', apiErrorMessage(err, 'Request failed'));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card space-y-3" data-testid="idp-claimed-domains">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <Globe className="w-4 h-4 text-primary-600" />
            Claimed domains — all tenants
            <span className="text-xs font-normal text-gray-400">(SuperAdmin)</span>
          </h2>
          <p className="text-xs text-gray-500">
            One owner per domain on this orchestrator. A tenant that gets "already claimed" is told nothing about the holder;
            resolve it here.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              className="input-base pl-7 py-1 text-sm w-56"
              placeholder="Filter by domain or tenant"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              aria-label="Filter claimed domains"
            />
          </div>
          <button type="button" className="p-1.5 rounded hover:bg-gray-100 text-gray-500" title="Refresh" onClick={() => void load()}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>
      {error && <p className="text-xs text-danger-700">{error}</p>}
      {shown.length === 0 ? (
        <p className="text-xs text-gray-400">{loading ? 'Loading…' : rows.length === 0 ? 'No domain is claimed on this orchestrator.' : 'No match.'}</p>
      ) : (
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500">
              <th className="py-2 pr-3">Domain</th>
              <th className="py-2 pr-3">Tenant</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3">Verified</th>
              <th className="py-2 pr-3 w-8" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {shown.map((r) => (
              <tr key={r.id} data-testid="idp-claimed-row" data-domain={r.domain}>
                <td className="py-1.5 pr-3">
                  <span className="font-medium text-gray-900">{domainLabel(r)}</span>
                  {r.domainUnicode && r.domainUnicode !== r.domain && <span className="ml-1 font-mono text-xs text-gray-500">{r.domain}</span>}
                </td>
                <td className="py-1.5 pr-3 text-xs text-gray-700">
                  {tenantName(r.tenantId)}
                  {tenantName(r.tenantId) !== r.tenantId && <span className="ml-1 font-mono text-gray-400">{r.tenantId}</span>}
                </td>
                <td className="py-1.5 pr-3"><DomainStatusBadge domain={r} /></td>
                <td className="py-1.5 pr-3 text-xs text-gray-500">
                  {r.verifiedAt ? `${formatDate(r.verifiedAt, dateSettings)}${r.verifiedBy ? ` by ${r.verifiedBy}` : ''}` : '—'}
                </td>
                <td className="py-1.5 pr-3">
                  <button
                    type="button"
                    className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-danger-600"
                    title="Remove this claim (with a reason)"
                    aria-label={`Remove the claim on ${domainLabel(r)}`}
                    onClick={() => void openRemove(r)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <RoutingPreviewDialog
        isOpen={!!target}
        title={target ? `Remove the claim on ${domainLabel(target)}` : ''}
        description={
          target ? (
            <span>
              Remove <strong>{domainLabel(target)}</strong> from tenant <strong>{tenantName(target.tenantId)}</strong>. Its users
              in that tenant stop signing in through the provider, and the domain can be claimed again.
            </span>
          ) : null
        }
        preview={preview}
        loading={previewLoading}
        error={previewError}
        busy={saving}
        variant="danger"
        confirmText="Remove claim"
        confirmDisabled={reason.trim().length < 3}
        onConfirm={() => void remove()}
        onCancel={() => setTarget(null)}
      >
        <div>
          <label htmlFor="idp-claim-remove-reason" className="block text-sm font-medium text-gray-700 mb-1">
            Reason <span className="text-danger-600">*</span>
          </label>
          <input
            id="idp-claim-remove-reason"
            className="input-base w-full"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            disabled={saving}
          />
          <p className="mt-1 text-xs text-gray-500">At least 3 characters; kept in the tenant's activity log.</p>
        </div>
      </RoutingPreviewDialog>
    </div>
  );
};

export default ClaimedDomainsCard;
